import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeProvider';

// ============================================================================
// Theme mode options
// ============================================================================

type ThemeMode = 'light' | 'dark' | 'system';

const THEME_OPTIONS: Array<{
  value: ThemeMode;
  label: string;
  description: string;
  icon: typeof Sun;
}> = [
  {
    value: 'light',
    label: 'Light',
    description: 'Bright, clean surfaces. Best in well-lit spaces.',
    icon: Sun,
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Cool, low-glare surfaces. Easier on the eyes at night.',
    icon: Moon,
  },
  {
    value: 'system',
    label: 'System',
    description: 'Follow your device appearance automatically.',
    icon: Monitor,
  },
];

// ============================================================================
// Main component
// ============================================================================

export function AppearanceSection() {
  const { theme, setTheme } = useTheme();
  const activeMode = theme.mode;

  return (
    <div className='rounded-lg border border-border p-5'>
      <h3 className='mb-1 text-sm font-semibold text-foreground'>Theme</h3>
      <p className='mb-4 text-xs text-muted-foreground'>
        Choose how Xonera looks. System follows your device setting.
      </p>

      <div
        role='radiogroup'
        aria-label='Theme'
        className='grid grid-cols-1 gap-3 sm:grid-cols-3'
      >
        {THEME_OPTIONS.map(({ value, label, description, icon: Icon }) => {
          const isActive = activeMode === value;
          return (
            <button
              key={value}
              type='button'
              role='radio'
              aria-checked={isActive}
              data-testid={`theme-option-${value}`}
              onClick={() => setTheme({ mode: value })}
              className={`relative flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-all duration-200 ${
                isActive
                  ? 'border-primary bg-accent ring-1 ring-primary'
                  : 'border-border hover:border-primary/40 hover:bg-accent/50'
              }`}
            >
              {isActive && (
                <span className='absolute right-3 top-3 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground'>
                  <Check className='h-3 w-3' />
                </span>
              )}
              <Icon
                className={`h-5 w-5 ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
              <span className='text-sm font-medium text-foreground'>
                {label}
              </span>
              <span className='text-xs text-muted-foreground'>
                {description}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
